import React, { useState, useMemo } from "react";
import "../Content/CSS/Seatmap.css";

const AedIcon = () => <span style={{ fontFamily: "sans-serif" }}>&#xea;</span>;

// Pehli valid (>0) price uthata hai, API field ka naam kuch bhi ho
const pickPrice = (...vals) => vals.map(Number).find((n) => n > 0) || 0;

const findAnyPrice = (s) => {
  const direct = pickPrice(s.TotalPrice, s.Price, s.SeatPrice, s.Amount, s.SeatFare, s.Fare);
  if (direct) return direct;
  const key = Object.keys(s).find(
    (k) => /price|amount|fare|charge/i.test(k) && Number(s[k]) > 0
  );
  return key ? Number(s[key]) : 0;
};

const normSeatMaster = (m) => ({
  ...m,
  SeatRows: (m.SeatRows || []).map((r) => ({
    ...r,
    AirlineSeats: (r.AirlineSeats || []).map((s) => ({
      ...s,
      TotalPrice: findAnyPrice(s),
    })),
  })),
});

export default function SeatMap({
  returnContractId = null,
  isB2C = false,
  useDummy = false,
  maxSeats = 1,
  inrToAed = null,
  ssrData = null,
  ssrLoading = false,
  ssrError = null,
  initialSelectedSeats = [],
  onDone,
  onClose,
}) {
  const [selectedSeats, setSelectedSeats] = useState(initialSelectedSeats);
  const [limitMsg, setLimitMsg] = useState("");
  const [activeSegmentIndex, setActiveSegmentIndex] = useState(0);

  // ---------- Dummy data (for testing/dev) ----------
  const dummySeatMaster = useMemo(() => {
    const cols = ["A", "B", "C", "D", "E", "F"];
    return {
      SouceCity: "Demo",
      DestinationCity: "Flight",
      AirlineCode: "6E",
      FlightNumber: "0000",
      SeatRows: Array.from({ length: 39 }, (_, i) => {
        const rowNum = i + 1;
        return {
          RowNumber: String(rowNum),
          AirlineSeats: cols.map((letter) => ({
            SeatCode: `${rowNum}${letter}`,
            IsAvailabe: Math.random() > 0.25,
            IsWindowSeat: letter === "A" || letter === "F",
            IsAisleSeat: letter === "C" || letter === "D",
            TotalPrice:
              letter === "A" || letter === "F"
                ? 1299
                : letter === "B" || letter === "E"
                ? 799
                : 449,
          })),
        };
      }),
    };
  }, []);

  // ---------- Segment list (one entry per flight leg) ----------
  const segmentList = useMemo(() => {
    if (useDummy) return [dummySeatMaster];

    const onward = (ssrData?.AirlineSeatMasterList || []).map((s) =>
      normSeatMaster({
        ...s,
        ContractId: s.ContractId ?? ssrData?.OnwardContractId ?? null,
      })
    );
    const ret = (ssrData?.ReturnAirlineSeatMasterList || []).map((s) =>
      normSeatMaster({
        ...s,
        ContractId: s.ContractId ?? returnContractId ?? null,
      })
    );

    return [...onward, ...ret];
  }, [useDummy, dummySeatMaster, ssrData, returnContractId]);

  const seatMaster = segmentList[activeSegmentIndex] || null;

  const loading = useDummy ? false : ssrLoading;
  const error = useDummy
    ? null
    : ssrError || (!ssrLoading && segmentList.length === 0 ? "No seat map available for this flight" : null);

  // ---------- Derived rows/columns/seatMap for the ACTIVE segment ----------
  const { rows, columns, seatMap, rowNumbers } = useMemo(() => {
    if (!seatMaster?.SeatRows) {
      return { rows: [], columns: [], seatMap: {}, rowNumbers: [] };
    }

    const rowsData = seatMaster.SeatRows;
    const colSet = new Set();
    const map = {};

    rowsData.forEach((row) => {
      row.AirlineSeats.forEach((seat) => {
        const letter = seat.SeatCode.replace(/^\d+/, "");
        colSet.add(letter);
        map[`${row.RowNumber}-${letter}`] = seat;
      });
    });

    const sortedCols = Array.from(colSet).sort();
    const sortedRowNums = rowsData
      .map((r) => r.RowNumber)
      .sort((a, b) => Number(a) - Number(b));

    return { rows: rowsData, columns: sortedCols, seatMap: map, rowNumbers: sortedRowNums };
  }, [seatMaster]);

  const displayColumns = useMemo(() => [...columns].reverse(), [columns]);

  const toAed = (inrAmount) => {
    if (!inrToAed) return null;
    return (Number(inrAmount || 0) * inrToAed).toFixed(2);
  };

  // ---------- Seat selection logic (per segment) ----------
  const toggleSeat = (seat) => {
    if (!seat || !seat.IsAvailabe) return;

    const exists = selectedSeats.some(
      (s) => s.seatCode === seat.SeatCode && s.segmentIndex === activeSegmentIndex
    );

    if (exists) {
      setSelectedSeats((prev) =>
        prev.filter((s) => !(s.seatCode === seat.SeatCode && s.segmentIndex === activeSegmentIndex))
      );
      setLimitMsg("");
      return;
    }

    const seatsInThisSegment = selectedSeats.filter((s) => s.segmentIndex === activeSegmentIndex);
    if (seatsInThisSegment.length >= maxSeats) {
      setLimitMsg(`You can select only ${maxSeats} seat${maxSeats > 1 ? "s" : ""} for this flight`);
      return;
    }

    setLimitMsg("");
    setSelectedSeats((prev) => [
      ...prev,
      {
        seatCode: seat.SeatCode,
        seatPrice: Number(seat.TotalPrice) || 0,
        segmentIndex: activeSegmentIndex,
        flightNumber: seatMaster?.FlightNumber,
        origin: seatMaster?.SouceCity,
        destination: seatMaster?.DestinationCity,
        contractId: seatMaster?.ContractId ?? null,
      },
    ]);
  };

  const totalPrice = selectedSeats.reduce((sum, s) => sum + s.seatPrice, 0);
  const currentSegmentSelectedCount = selectedSeats.filter((s) => s.segmentIndex === activeSegmentIndex).length;

  // ---------- Per-segment totals ----------
  const segmentTotals = useMemo(() => {
    return segmentList.map((seg, idx) => {
      const seatsForSeg = selectedSeats.filter((s) => s.segmentIndex === idx);
      const inrTotal = seatsForSeg.reduce((sum, s) => sum + s.seatPrice, 0);
      return {
        segmentIndex: idx,
        label: `${seg.SouceCity} → ${seg.DestinationCity}`,
        inrTotal,
        aedTotal: inrToAed ? Number(toAed(inrTotal)) : null,
        seatCount: seatsForSeg.length,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segmentList, selectedSeats, inrToAed]);

  return (
    <div
      className="offcanvas offcanvas-end show"
      tabIndex="-1"
      style={{ width: "1100px", maxWidth: "98vw", zIndex: 1070 }}
    >
      <div className="offcanvas-header border-bottom">
        <div>
          <span className="eyebrow">Select seat</span>
          <h5>
            {seatMaster ? `${seatMaster.SouceCity} → ${seatMaster.DestinationCity}` : "Choose your seat"}
          </h5>
        </div>
        <button type="button" className="close-btn" aria-label="Close" onClick={onClose}>
          ✕
        </button>
      </div>

      {/* Segment tabs — only show if more than 1 flight leg */}
      {segmentList.length > 1 && (
        <div className="d-flex border-bottom" style={{ padding: "8px 16px", gap: "8px", flexWrap: "wrap" }}>
          {segmentList.map((seg, idx) => {
            const selCount = selectedSeats.filter((s) => s.segmentIndex === idx).length;
            return (
              <button
                key={idx}
                className={`btn ${activeSegmentIndex === idx ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => setActiveSegmentIndex(idx)}
                type="button"
              >
                {seg.SouceCity} → {seg.DestinationCity} ({seg.AirlineCode}-{seg.FlightNumber})
                {selCount > 0 && ` ✓ ${selCount}`}
              </button>
            );
          })}
        </div>
      )}

      {/* Legend */}
      {!loading && !error && rows.length > 0 && (
        <div className="seat-legend">
          <span className="legend-item">
            <span className="legend-swatch available" /> Available
          </span>
          <span className="legend-item">
            <span className="legend-swatch selected" /> Selected
          </span>
          <span className="legend-item">
            <span className="legend-swatch occupied" /> Occupied
          </span>
        </div>
      )}

      <div className="offcanvas-body">
        {loading && <div className="p-4 text-center">Loading seat map...</div>}
        {!loading && error && <div className="p-4 text-center text-danger">{error}</div>}

        {limitMsg && (
          <div className="p-2 text-center" style={{ color: "#b91c1c", fontWeight: 600 }}>
            {limitMsg}
          </div>
        )}

        {!loading && !error && rows.length > 0 && (
          <div className="seat-container">
            <div className="cockpit-box">
              <img
                src="https://www.tripcaliber.com/Content/Images/flight_cockpit.png"
                alt="Cockpit"
                className="cockpit-img"
              />
              <div className="cockpit-shape"><span>COCKPIT</span></div>
            </div>

            <div className="seat-scroll">
              <div className="seat-header">
                <div className="seat-letter-space"></div>
                {rowNumbers.map((rn) => (
                  <div key={rn} className="seat-number">{rn}</div>
                ))}
              </div>

              {displayColumns.map((letter) => (
                <div className="seat-line" key={letter}>
                  <div className="seat-letter">{letter}</div>

                  {rowNumbers.map((rn) => {
                    const seat = seatMap[`${rn}-${letter}`];
                    if (!seat) {
                      return <div key={`${rn}-${letter}`} className="seat-box seat-gap" />;
                    }

                    const selected = selectedSeats.some(
                      (s) => s.seatCode === seat.SeatCode && s.segmentIndex === activeSegmentIndex
                    );
                    const aedPrice = toAed(seat.TotalPrice);

                    return (
                      <button
                        key={seat.SeatCode}
                        type="button"
                        className={`seat-box
                          ${seat.IsAvailabe ? "available" : "occupied"}
                          ${selected ? "selected-seat" : ""}
                        `}
                        title={
                          aedPrice
                            ? `${seat.SeatCode} - AED ${aedPrice}`
                            : `${seat.SeatCode} - ₹${seat.TotalPrice}`
                        }
                        aria-pressed={selected}
                        aria-label={`Seat ${seat.SeatCode}, ${seat.IsAvailabe ? "available" : "occupied"}${selected ? ", selected" : ""}`}
                        onClick={() => toggleSeat(seat)}
                      >
                        <span>{seat.SeatCode}</span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>

            <div className="tail-box">
              <img
                src="https://www.tripcaliber.com/Content/Images/flight_tail.png"
                alt="Tail"
                className="tail-img"
              />
            </div>
          </div>
        )}
      </div>

      <div className="offcanvas-bottom p-3 border-top">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <strong>
              Selected ({currentSegmentSelectedCount}/{maxSeats}
              {segmentList.length > 1 ? " this flight" : ""}):
            </strong>{" "}
            {selectedSeats.length
              ? selectedSeats
                  .map((s) => (segmentList.length > 1 ? `${s.origin}-${s.destination}: ${s.seatCode}` : s.seatCode))
                  .join(", ")
              : "None"}
          </div>

          <div className="d-flex align-items-center gap-3">
            {segmentList.length > 1 ? (
              <div className="segment-price-breakdown">
                {segmentTotals.map((st) => (
                  <div className="segment-price-row" key={st.segmentIndex}>
                    <span className="segment-price-label">{st.label}</span>
                    <span className="segment-price-value">
                      {inrToAed ? (
                        <><AedIcon /> {st.aedTotal.toLocaleString("en-AE")}</>
                      ) : (
                        <>₹{st.inrTotal}</>
                      )}
                    </span>
                  </div>
                ))}
                <div className="segment-price-row segment-price-total">
                  <span className="segment-price-label">Total</span>
                  <span className="segment-price-value">
                    {inrToAed ? (
                      <><AedIcon /> {Number(toAed(totalPrice)).toLocaleString("en-AE")}</>
                    ) : (
                      <>₹{totalPrice}</>
                    )}
                  </span>
                </div>
              </div>
            ) : (
              <h4 className="mb-0">
                {inrToAed ? (
                  <><AedIcon /> {Number(toAed(totalPrice)).toLocaleString("en-AE")}</>
                ) : (
                  <>₹{totalPrice}</>
                )}
              </h4>
            )}

            <button
              className="btn btn-primary px-4"
              onClick={() => onDone(selectedSeats, totalPrice)}
              disabled={loading}
              type="button"
            >
              DONE
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}